import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Language = 'uz' | 'ru' | 'en';

export type ServiceKind =
  | 'instagram_verification'
  | 'telegram_premium'
  | 'telegram_stars'
  | 'telegram_gifts'
  | 'telegram_nft'
  | 'telegram_verification';

export interface UserRestrictions {
  services: ServiceKind[];
  noStats: boolean;
  noOrders: boolean;
  noReferral: boolean;
}

export interface AuthUser {
  id: string;
  telegramId: string;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  photoUrl: string | null;
  language: Language;
  phone: string | null;
  balance: string;
  referralCode: string;
  // Admin-set capability restrictions; absent on older sessions → treat as none.
  restrictions?: UserRestrictions;
}

export const EMPTY_RESTRICTIONS: UserRestrictions = {
  services: [],
  noStats: false,
  noOrders: false,
  noReferral: false,
};

interface AuthState {
  accessToken: string | null;
  user: AuthUser | null;
  language: Language;
  setSession: (token: string, user: AuthUser) => void;
  setLanguage: (lang: Language) => void;
  updateUser: (patch: Partial<AuthUser>) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      user: null,
      language: 'uz',
      setSession: (token, user) =>
        set({ accessToken: token, user, language: user.language }),
      setLanguage: (lang) => set({ language: lang }),
      updateUser: (patch) =>
        set((s) => (s.user ? { user: { ...s.user, ...patch } } : {})),
      clear: () => set({ accessToken: null, user: null }),
    }),
    {
      name: 'premium-bot-auth',
      partialize: (s) => ({ accessToken: s.accessToken, user: s.user, language: s.language }),
    },
  ),
);

/**
 * Current user's restrictions, always fully populated (falls back to "none"
 * for older sessions whose stored user predates the field). Use in UI to gate
 * locked services, stats, and the referral link.
 */
export function useRestrictions(): UserRestrictions {
  return useAuthStore((s) => s.user?.restrictions) ?? EMPTY_RESTRICTIONS;
}
